import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';

import Validator, { useValidate, checkValidity } from '../../components/validator';
import ConfirmationDialog from '../../components/confirmationdlg';

import CMCsService from '../../data/cmcs-service';


const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
        minHeight: '36px',
    },
    sensorConfigs: {
        width: '100%',
        background: theme.palette.background.default,
        padding: theme.spacing(3),
        height: '100%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    backdrop: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 10000,
    },
}));

const AddCMC = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams();
    const [cmc, setCMC] = useState({
        name: '',
        host: '',
        password: '',
    });
    const [disableCreate, setDisableCreate] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const cmcForm = useValidate({});
    const [original, setOriginal] = useState();
    const [isUpdate, setIsUpdate] = useState(false);
    const [init, setInit] = useState(true);

    useEffect(() => {
        if(params && params.uuid) {
            getCMC();
            setIsUpdate(true);
        }
    }, []);

    const getCMC = () => {
        CMCsService.get(params.uuid).then(res => {
            setCMC(res);
        });
    };

    const handleText = (e, validID, valid) => {
        cmcForm.set({...cmcForm.get(), [validID]: valid});

        setCMC({
            ...cmc,
            [e.target.name]: e.target.value,
        });
    };

    const handleCreate = () => {
        let c = {...cmc};

        if(isUpdate) {
            CMCsService.rename(params.uuid, c.name).then(res => {
                toast.success('CMC name updated successfully');
                navigate('/cmcs');
            }, err => {
                toast.error('Unable to update CMC name...');
                console.error(err);
            });
        } else {
            CMCsService.register(c.name, {
                host: c.host,
                password: c.password,
            }).then(res => {
                toast.success('CMC registered successfully');
                navigate('/cmcs');
            }, err => {
                toast.error('Unable to register CMC...');
                console.error(err);
            })
        }
    };

    const handleCancel = () => {
        navigate('/cmcs');
    };

    const handleOpenDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleDeleteCMC = () => {
        CMCsService.delete(params.uuid).then(() => {
            toast.success('CMC deleted successfully');
            navigate(-1);
        }, err => {
            toast.error(err);
            setDeleteOpen(false);
        });
    };

    return (
        <Grid container>
            <Grid item xs={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={4}>
                            <Grid item xs={10}></Grid>
                            <Grid item xs={2}>
                                <Box display={isUpdate ? 'block' : 'none'}>
                                    <Button
                                        className={classes.buttonRight}
                                        variant="contained"
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={handleOpenDelete}
                                    >
                                        <DeleteIcon />
                                        Delete
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container spacing={5}>
                            <Grid container item xs={8}>
                                <Grid item xs={12}>
                                    <Box hidden={isUpdate}>
                                        <Validator
                                            valueType="host"
                                            errorText="Hostname or IP is invalid"
                                            fullWidth
                                            label="Host"
                                            name="host"
                                            value={cmc.host}
                                            onChange={handleText}
                                        />
                                    </Box>
                                    <Box mt={isUpdate ? 0 : 3}>
                                        <Validator
                                            valueType="sensorName"
                                            errorText="CMC name is invalid"
                                            fullWidth
                                            label="Name"
                                            name="name"
                                            value={cmc.name}
                                            onChange={handleText}
                                        />
                                    </Box>
                                    <Box mt={3} hidden={isUpdate}>
                                        <Validator
                                            allowEmpty={isUpdate}
                                            errorText="Password cannot be empty"
                                            type="password"
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            value={cmc.password}
                                            onChange={handleText}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} container justifyContent="flex-end">
                                <Box ml={2}><Button variant="contained" aria-label="cancel" onClick={handleCancel}>Back</Button></Box>
                                <Box ml={2}>
                                    <Button
                                        className={classes.buttonRight}
                                        variant="contained"
                                        color="primary"
                                        aria-label={isUpdate ? 'update' : 'add'}
                                        onClick={handleCreate}
                                        disabled={!checkValidity(cmcForm.get()) || disableCreate}
                                    >
                                        {isUpdate ? 'Update' : 'Add'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                        <ConfirmationDialog
                            onClose={handleOpenDelete}
                            onOK={handleDeleteCMC}
                            open={deleteOpen}
                            messageggg={(() => {
                                return `Are you sure you want to delete the cmc ${cmc && (cmc.name || cmc.host)}?
                                Any MC2 policies assign on that CMC will be replaced with default policies.`;
                            })()}
                            message={(() => {
                                return <div>
                                    {`Are you sure you want to delete the cmc ${cmc && (cmc.name || cmc.host)}?`}
                                    <br/>
                                    <br/>
                                    All MC2 policies assign to sensors on that CMC will be replaced with default policies.
                                </div>;
                            })()}
                            title="Delete CMC"
                            cancelLabel="Cancel"
                            okLabel="Continue"
                        />
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default AddCMC;
